import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AbstractControl, FormBuilder, FormGroup, FormArray, Validators } from '@angular/forms';
import { finalize } from 'rxjs/operators';
import { EnrollmentService } from '../../../services/enrollment.service';
import { PaymentPlansService } from '../../../services/payment-plans.service';
import { ToastService } from '../../../services/toast.service';
import { LoadingService } from '../../../services/loading.service';
import { PaymentPlanRefinanceService } from '../../../services/payment-plan-refinance.service';
import {
  EditablePaymentPlanVersionReasons,
  PaymentPlanVersionReason,
  PaymentPlanVersionReasonLabels
} from '../../../enums/PaymentPlanVersionReason';

@Component({
  selector: 'app-edit-payment-plans',
  templateUrl: './edit-payment-plans.component.html',
  styleUrls: ['./edit-payment-plans.component.scss']
})
export class EditPaymentPlansComponent implements OnInit {
  enrollmentForm!: FormGroup;
  enrollmentId!: number;
  totalAssignedAmount = 0;
  remainingBalance = 0;
  amountPaid = 0;
  isExactMatch = false;
  exceedsTotalAmount = false;

  showRefinanceModal = false;
  refinanceInstallments = 2;
  minInstallments = 1;
  maxInstallments = 12;
  partiallyPaidCount = 0;

  // 🆕 Baseline inmutable para recompute
  private baselineEditable: any[] = [];
  private baselineTotalAmount = 0;
  private baselineRemainingBalance = 0;
  private baselineAmountPaid = 0;
  addedInstallments: any[] = [];
  private unplacedRedistributes = new Set<number>();

  // 🆕 Modal auto-crear cuota
  showCreateInstallmentModal = false;
  pendingPartialPlan: any = null;
  pendingShortfall = 0;
  newInstallmentDate = '';

  // 🏢 Información de inscripción corporativa
  isCorporateEnrollment = false;
  corporateInfo: any = null;
  participantInfo: any = null;

  whoPaysOptions = [
    { value: 1, label: 'Empresa' },
    { value: 2, label: 'Participante' },
    { value: 3, label: 'Compartido' }
  ];

  // 🔧 Exponer Math para uso en template
  Math = Math;

  // 🆕 Modal de motivo + comentario al guardar (versionado por grupo)
  showReasonModal = false;
  reasonChoices = EditablePaymentPlanVersionReasons.map(r => ({
    value: r,
    label: PaymentPlanVersionReasonLabels[r]
  }));
  selectedReason: PaymentPlanVersionReason = PaymentPlanVersionReason.Refinance;
  reasonComment = '';
  PaymentPlanVersionReason = PaymentPlanVersionReason;

  constructor(
    private route: ActivatedRoute,
    private fb: FormBuilder,
    private enrollmentService: EnrollmentService,
    private paymentPlansService: PaymentPlansService,
    private toastService: ToastService,
    private loadingService: LoadingService,
    private router: Router,
    private refinanceSvc: PaymentPlanRefinanceService
  ) { }

  // ──────────────────────────────────────────────────────────────────────
  // 🆕 Estado para el snapshot completo (PAID + PARTIAL + PENDING)
  // ──────────────────────────────────────────────────────────────────────
  excessSummary: { installmentNumber: number; receiptId: number; excessPaymentId: number; amount: number }[] = [];
  paidInstallments: any[] = [];        // cuotas READ_ONLY
  partialInstallments: any[] = [];     // cuotas CLOSE_OR_KEEP — el operador elige acción
  showPaidSection = false;             // colapsada por defecto
  showPartialSection = true;
  redistributionLogLines: string[] = []; // se acumula al usar "cerrar y redistribuir"

  /** Acción elegida por el operador para cada cuota PARTIALLY_PAID */
  partialActions: Record<number, 'keep' | 'close-redistribute' | 'close-loss'> = {};

  /**
   * 🆕 Cuotas ordenadas ascendente por número, manteniendo el índice REAL
   * del FormArray para que `[formGroupName]` siga apuntando al control correcto.
   * Esto evita reordenar el FormArray (lo que dispararía valueChanges y rompería
   * suscripciones), pero presenta las cuotas en orden natural al operador.
   */
  get sortedPaymentPlansWithIndex(): Array<{ ctrl: AbstractControl; idx: number }> {
    return this.paymentPlans.controls
      .map((ctrl, idx) => ({ ctrl, idx }))
      .sort((a, b) => {
        const na = Number(a.ctrl.get('installmentNumber')?.value || 0);
        const nb = Number(b.ctrl.get('installmentNumber')?.value || 0);
        return na - nb;
      });
  }

  /**
   * 🆕 trackBy estable para el *ngFor de cuotas. Sin esto, Angular destruye y
   * recrea los <input> en cada change detection cycle (porque el getter
   * `sortedPaymentPlansWithIndex` retorna objetos nuevos), lo que hace que se
   * pierda el foco con cada tecla y que el calendar no permita seleccionar fecha.
   */
  trackByIdx = (_: number, item: { ctrl: AbstractControl; idx: number }) => item.ctrl;

  /**
   * 🆕 Redistribuye el saldo restante entre las cuotas EDITABLES posteriores
   * a la cuota en `idx`. Pensado para que el operador fije a mano los montos
   * de las primeras N cuotas y dispare el ajuste automático del resto.
   *
   * Algoritmo:
   *   1. Sumar lo "fijo" del plan: PAID del snapshot + cuotas en FormArray con
   *      installmentNumber ≤ current (a su amountToPay) + locked + PARTIAL keep.
   *   2. saldoRestante = totalAmount - fijo.
   *   3. Distribuir prorrateado igualitario entre cuotas EDITABLES con
   *      installmentNumber > current. Última absorbe redondeo.
   */
  recalculateFromInstallment(currentIdx: number): void {
    const currentCtrl = this.paymentPlans.at(currentIdx);
    const currentNumber = Number(currentCtrl.get('installmentNumber')?.value || 0);

    // 1) Fijo: PAID del snapshot (inmutables, fuera del FormArray)
    let frozen = this.paidInstallments.reduce(
      (s, p) => s + (Number(p.amountToPay) || 0), 0);

    // 2) Fijo: cuotas en FormArray que NO se deben recalcular
    this.paymentPlans.controls.forEach(c => {
      const num = Number(c.get('installmentNumber')?.value || 0);
      const status = c.get('paymentStatus')?.value;
      const amount = this.parseFormattedCurrency(c.get('amountToPay')?.value || 0);
      const isLocked = this.isInstallmentLocked(c);
      // Locked (cerradas), PARTIAL keep (mantenida) o ≤ current → no se tocan
      if (isLocked || status === 'PARTIALLY_PAID' || num <= currentNumber) {
        frozen += amount;
      }
    });

    // 3) Candidatas a recalcular: editables con número > current
    const candidates = this.paymentPlans.controls.filter(c => {
      const num = Number(c.get('installmentNumber')?.value || 0);
      const status = c.get('paymentStatus')?.value;
      return num > currentNumber
          && !this.isInstallmentLocked(c)
          && status !== 'PARTIALLY_PAID'
          && (status === 'PENDING' || status === 'OVERDUE' || status === 'LINK_GENERATED');
    });

    if (candidates.length === 0) {
      this.toastService.showToast('warning', 'No hay cuotas editables posteriores para recalcular.');
      return;
    }

    const totalAmount = Number(this.enrollmentForm.get('totalAmount')?.value || 0);
    const remainingToDistribute = this.roundToTwo(totalAmount - frozen);

    if (remainingToDistribute <= 0) {
      this.toastService.showToast('warning',
        'Las cuotas anteriores ya cubren el total. Las posteriores no necesitan cambios.');
      return;
    }

    const amounts = this.refinanceSvc.splitEvenly(remainingToDistribute, candidates.length);
    candidates.forEach((c, i) => {
      c.get('amountToPay')?.setValue(amounts[i]);
    });

    this.validateAssignedAmount();
    const formatted = remainingToDistribute.toLocaleString('es-CO',
      { style: 'currency', currency: 'COP', minimumFractionDigits: 0, maximumFractionDigits: 0 });
    this.toastService.showToast('success',
      `Saldo de ${formatted} redistribuido en ${candidates.length} cuota${candidates.length === 1 ? '' : 's'} posterior${candidates.length === 1 ? '' : 'es'}.`);
  }

  ngOnInit(): void {
    this.enrollmentId = Number(this.route.snapshot.paramMap.get('id'));
    this.initForm();
    this.fetchEnrollmentData();

    // 🔧 Exponer componente para debug global (solo desarrollo)
    (window as any).debugComponent = this;
    console.log('🔧 Componente expuesto globalmente. Usar en consola: debugComponent.debugValidation()');
  }

  initForm() {
    this.enrollmentForm = this.fb.group({
      totalAmount: ['', Validators.required],
      paymentPlans: this.fb.array([])
    });
  }

  private formatDateToInput(date: string | Date): string {
    const d = new Date(date);
    const year = d.getFullYear();
    const month = ('0' + (d.getMonth() + 1)).slice(-2);
    const day = ('0' + d.getDate()).slice(-2);
    return `${year}-${month}-${day}`;
  }

  get paymentPlans(): FormArray {
    return this.enrollmentForm.get('paymentPlans') as FormArray;
  }

  createPaymentPlanGroup(): FormGroup {
    return this.fb.group({
      id: [null],
      dueDate: ['', Validators.required],
      installmentNumber: [1, Validators.required],
      amountToPay: ['', Validators.required],
      amountPaid: [0],
      paymentStatus: ['PENDING', Validators.required],
      payerType: ['PARTICIPANT', Validators.required],
      isPunished: [false],
      observations: [''],
      isLocked: [false]
    });
  }

  fetchEnrollmentData(): void {
    this.loadingService.show();

    Promise.all([
      this.enrollmentService.getById(this.enrollmentId).toPromise(),
      this.paymentPlansService.getEditableSnapshot(this.enrollmentId, 'individual').toPromise()
    ])
      .then(([enrollmentResponse, snapshotRes]) => {
        const enrollment = enrollmentResponse.data;
        const snapshot = snapshotRes?.data ?? snapshotRes;
        const allInstallments: any[] = snapshot?.installments ?? [];

        this.enrollmentForm.patchValue({ totalAmount: enrollment.totalAmount });
        this.remainingBalance = enrollment.remainingBalance;
        this.amountPaid = enrollment.amountPaid;

        // Detección corporativa: si hay cuotas COMPANY mezcladas, ocultamos las
        // de empresa (esa pantalla es solo del PARTICIPANTE).
        const hasCompanyPlans = allInstallments.some((p: any) => p.payerType === 'COMPANY');
        const isCorporateByEnrollment = enrollment.isCompany && enrollment.corporateAgreementId;
        if (hasCompanyPlans || isCorporateByEnrollment) {
          this.isCorporateEnrollment = true;
          this.corporateInfo = {
            agreementId: enrollment.corporateAgreementId || 'N/A',
            companyName: enrollment.company?.name || 'Empresa Corporativa',
            companyNIT: enrollment.company?.documentId || 'N/A',
            projectName: enrollment.project?.name || 'N/A'
          };
          this.participantInfo = {
            name: `${enrollment.participant?.firstName || ''} ${enrollment.participant?.lastName || ''}`.trim() || 'Participante',
            document: `${enrollment.participant?.typeDocument || ''} ${enrollment.participant?.documentId || ''}`.trim() || 'N/A',
            email: enrollment.participant?.email || 'N/A'
          };
        }

        const visibleInstallments = (hasCompanyPlans || isCorporateByEnrollment)
          ? allInstallments.filter((p: any) => p.payerType === 'PARTICIPANT')
          : allInstallments;

        this.paidInstallments = visibleInstallments.filter(p => p.editability === 'READ_ONLY');
        this.partialInstallments = visibleInstallments.filter(p => p.editability === 'CLOSE_OR_KEEP');
        const editable = visibleInstallments.filter(p => p.editability === 'EDITABLE');

        // Baseline inmutable. Escalares vienen del backend (no Σ).
        this.baselineEditable = editable.map(p => ({ ...p }));
        this.baselineTotalAmount = Number(enrollment.totalAmount) || 0;
        this.baselineRemainingBalance = Number(enrollment.remainingBalance) || 0;
        this.baselineAmountPaid = Number(enrollment.amountPaid) || 0;
        this.addedInstallments = [];

        this.partialActions = {};
        this.partialInstallments.forEach(p => this.partialActions[p.id] = 'keep');

        this.rebuildFromBaseline();
        setTimeout(() => this.validateAssignedAmount(), 100);
      })
      .catch((err) => {
        console.error('Error loading enrollment info', err);
        this.toastService.showToast('error', 'Error cargando los datos de la inscripción.');
      })
      .finally(() => {
        this.loadingService.hide();
      });
  }

  subscribeToAllPaymentPlanChanges(): void {
    console.log('🔔 SUSCRIBIENDO A CAMBIOS - Total cuotas existentes:', this.paymentPlans.controls.length);
    this.paymentPlans.controls.forEach((control, index) => {
      control.valueChanges.subscribe(() => {
        console.log(`🔔 CAMBIO DETECTADO en cuota existente ${index + 1}`);
        this.validateAssignedAmount();
      });
    });
  }


  validateAssignedAmount(): void {
    console.log('🔄 INICIANDO VALIDACIÓN - Número de cuotas:', this.paymentPlans.controls.length);

    // SUMATORIA DE SALDO PENDIENTE (Monto a pagar - Dinero ya ingresado)
    // Σ max(0, amountToPay - amountPaid). Delegado al servicio compartido.
    const totalPending = this.refinanceSvc.totalAssignedPending(
      this.paymentPlans.controls.map(c => c.getRawValue())
    );

    console.log('🔢 SUMA PENDIENTE (A distribuir):', totalPending);

    const remaining = this.roundToTwo(this.remainingBalance);

    // El total asignado debe ser lo que el usuario declara en la grilla que le deben
    this.totalAssignedAmount = totalPending;
    this.exceedsTotalAmount = totalPending - remaining > 0.01;
    this.isExactMatch = this.refinanceSvc.isBalanced(totalPending, remaining);

    // 🔍 DEBUG CRÍTICO - Estado del botón
    console.log('🔍 ESTADO DE VALIDACIÓN FINAL:', {
      totalAssignedPending: totalPending,
      remainingBalance: remaining,
      isExactMatch: this.isExactMatch,
      exceedsTotalAmount: this.exceedsTotalAmount,
      formValid: this.enrollmentForm.valid,
      buttonEnabled: this.enrollmentForm.valid && this.isExactMatch && !this.exceedsTotalAmount,
      paymentPlansLength: this.paymentPlans.controls.length
    });

    // Mostrar detalles de cada control del formulario si hay errores
    if (!this.enrollmentForm.valid) {
      console.log('❌ ERRORES DE FORMULARIO:', this.getFormValidationErrors());
    }
  }

  /**
   * 🔍 Obtener errores detallados del formulario para debugging
   */
  private getFormValidationErrors(): any {
    const errors: any = {};

    // Errores del formulario principal
    Object.keys(this.enrollmentForm.controls).forEach(key => {
      const controlErrors = this.enrollmentForm.get(key)?.errors;
      if (controlErrors) {
        errors[key] = controlErrors;
      }
    });

    // Errores de los planes de pago
    this.paymentPlans.controls.forEach((control, index) => {
      const formGroup = control as FormGroup;
      Object.keys(formGroup.controls).forEach(fieldKey => {
        const fieldControl = formGroup.get(fieldKey);
        if (fieldControl?.errors) {
          errors[`paymentPlan[${index}].${fieldKey}`] = fieldControl.errors;
        }
      });
    });

    return errors;
  }

  roundToTwo(value: number): number {
    return this.refinanceSvc.roundToTwo(value);
  }

  /**
   * 🆕 Suma del saldo pendiente (shortfall) de las cuotas PARTIALLY_PAID que el
   * operador decidió MANTENER ('keep'). Siguen en el FormArray y ya aportan ese
   * shortfall al total vía validateAssignedAmount (max(0, amountToPay - amountPaid)).
   * Por eso NO debe volver a repartirse entre las cuotas nuevas al refinanciar:
   * `remainingBalance` ya lo incluye, así que distribuir `remainingBalance` completo
   * lo contaría dos veces y descuadraría el total (botón Guardar bloqueado).
   */
  private getPreservedPartialShortfall(): number {
    return this.refinanceSvc.preservedPartialShortfall(
      this.paymentPlans.controls.map(c => c.getRawValue())
    );
  }

  isInstallmentLocked(ctrl: AbstractControl): boolean {
    // Una cuota se considera bloqueada si:
    //   - El backend la marcó IsLocked (cuota PAID arrastrada a la versión nueva)
    //   - Está parcialmente pagada
    //   - O tiene amountPaid > 0 (ya hay recibos asociados)
    return !!ctrl.get('isLocked')?.value
      || ctrl.get('paymentStatus')?.value === 'PARTIALLY_PAID'
      || (ctrl.get('amountPaid')?.value || 0) > 0;
  }




  /**
   * 🆕 Primer número de cuota libre considerando TODO el universo:
   *   - cuotas PAID del snapshot (NO están en el FormArray pero su número está reservado)
   *   - cuotas PARTIAL del snapshot (sí están en el FormArray)
   *   - cuotas EDITABLES del FormArray
   * La numeración nunca decrece y nunca pisa una cuota fija (pagada/parcial).
   */
  private get firstFreeInstallmentNumber(): number {
    return this.refinanceSvc.firstFreeInstallmentNumber(
      this.paymentPlans.controls.map(c => c.get('installmentNumber')?.value),
      this.paidInstallments.map(p => p.installmentNumber)
    );
  }

  addPaymentPlan(): void {
    const newPlan = this.createPaymentPlanGroup();
    // Usamos el siguiente número libre, NO `length + 1`, para no colisionar
    // con cuotas PAID del snapshot que viven fuera del FormArray.
    newPlan.get('installmentNumber')?.setValue(this.firstFreeInstallmentNumber);

    newPlan.valueChanges.subscribe(() => this.validateAssignedAmount());
    this.paymentPlans.push(newPlan);
    this.validateAssignedAmount();
  }

  removePaymentPlan(index: number): void {
    const ctrl = this.paymentPlans.at(index);
    const num = ctrl.get('installmentNumber')?.value;
    const isAdded = !ctrl.get('id')?.value;
    if (isAdded) {
      const addedIdx = this.addedInstallments.findIndex(a => a.installmentNumber === num);
      if (addedIdx >= 0) this.addedInstallments.splice(addedIdx, 1);
    }
    this.paymentPlans.removeAt(index);
    this.validateAssignedAmount();
  }

  /**
   * @deprecated Ya no se renumeran cuotas dinámicamente. Los números son fijos por
   * cuota (las pagas/parciales conservan el original; las nuevas heredan
   * `firstFreeInstallmentNumber`). Se mantiene la firma por compat con código viejo
   * pero ahora es no-op.
   */
  updateInstallmentNumbers(): void {
    // intencionalmente vacío
  }

  showRecalculateButton(): boolean {
    const isDecimal = this.totalAssignedAmount % 1 !== 0;
    return isDecimal && this.paymentPlans.length > 1;
  }

  recalculateInstallmentsEvenly(): void {
    const count = this.paymentPlans.length;
    if (count === 0) return;

    // Identificar cuotas bloqueadas (pagadas parcialmente) vs cuotas libres
    const lockedPlans: number[] = [];
    const freePlans: number[] = [];

    this.paymentPlans.controls.forEach((ctrl, index) => {
      if (this.isInstallmentLocked(ctrl)) {
        lockedPlans.push(index);
      } else {
        freePlans.push(index);
      }
    });

    if (freePlans.length === 0) {
      this.toastService.showToast('warning', 'No hay cuotas libres para recalcular.');
      return;
    }

    // El monto a distribuir igualitariamente es el Saldo Restante
    const amounts = this.refinanceSvc.splitEvenly(this.remainingBalance, freePlans.length);

    freePlans.forEach((planIndex, loopIndex) => {
      const formattedValue = amounts[loopIndex].toLocaleString('es-CO', {
        style: 'currency',
        currency: 'COP',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });

      const ctrl = this.paymentPlans.at(planIndex);
      ctrl.get('amountToPay')?.setValue(formattedValue);
      ctrl.get('amountToPay')?.markAsTouched();
    });

    this.validateAssignedAmount();
    this.toastService.showToast('success', 'Cuotas libres recalculadas correctamente.');
  }

  // 🆕 ===== MÉTODOS PARA MODAL DE REFINANCIACIÓN =====

  /**
   * Mostrar modal de refinanciación
   */
  openRefinanceModal(): void {
    // partiallyPaidCount queda solo informativo (las parciales se preservan
    // aparte ahora; ya no entran al cálculo de N cuotas a generar).
    this.partiallyPaidCount = this.paymentPlans.controls
      .filter(ctrl => this.isInstallmentLocked(ctrl)
                   || ctrl.get('paymentStatus')?.value === 'PARTIALLY_PAID').length;

    this.minInstallments = 1;
    const currentEditableCount = this.paymentPlans.controls
      .filter(ctrl => !this.isInstallmentLocked(ctrl)
                   && ctrl.get('paymentStatus')?.value !== 'PARTIALLY_PAID').length;
    this.refinanceInstallments = Math.max(this.minInstallments, currentEditableCount || 1);
    this.showRefinanceModal = true;
  }

  /**
   * Cerrar modal de refinanciación
   */
  closeRefinanceModal(): void {
    this.showRefinanceModal = false;
  }

  /**
   * Confirmar y aplicar refinanciación automática
   */
  confirmRefinance(): void {
    if (this.refinanceInstallments < this.minInstallments || this.refinanceInstallments > this.maxInstallments) {
      this.toastService.showToast('error', `El número de cuotas debe estar entre ${this.minInstallments} y ${this.maxInstallments}.`);
      return;
    }

    this.applyRefinancing();
    this.closeRefinanceModal();
  }

  /**
   * Aplicar refinanciación automática con el número de cuotas especificado.
   *
   * 🆕 Reglas con numeración estable:
   *   - Las cuotas PAID del snapshot NO se tocan (viven fuera del FormArray).
   *   - Las cuotas PARTIAL ya están en el FormArray con isLocked=true (close-*)
   *     o como PARTIALLY_PAID (keep). Las preservamos tal cual.
   *   - Las cuotas EDITABLES (PENDING/OVERDUE/LINK_GENERATED, no locked) se
   *     borran y se reemplazan por N cuotas nuevas con números empezando en
   *     `firstFreeInstallmentNumber`.
   */
  private applyRefinancing(): void {
    if (this.remainingBalance <= 0) {
      this.toastService.showToast('warning', 'No hay saldo pendiente para refinanciar.');
      return;
    }

    const newInstallmentsCount = this.refinanceInstallments;
    if (newInstallmentsCount <= 0) {
      this.toastService.showToast('error', 'Debe haber al menos una cuota nueva.');
      return;
    }

    // 1) Snapshot de las cuotas PARTIAL (locked o keep) — las preservamos
    const preserved = this.paymentPlans.controls
      .filter(ctrl => this.isInstallmentLocked(ctrl) || ctrl.get('paymentStatus')?.value === 'PARTIALLY_PAID')
      .map(ctrl => ctrl.getRawValue());

    // 1.b) El shortfall de las parciales preservadas en 'keep' ya está incluido en
    // remainingBalance y lo vuelve a contar el validador. Solo repartimos el resto.
    const amountToDistribute = this.roundToTwo(this.remainingBalance - this.getPreservedPartialShortfall());
    if (amountToDistribute <= 0) {
      this.toastService.showToast('warning',
        'Las cuotas parciales mantenidas ya cubren el saldo restante. No hay monto para repartir en cuotas nuevas.');
      return;
    }

    // 2) Limpiar el FormArray
    while (this.paymentPlans.length !== 0) this.paymentPlans.removeAt(0);

    // 3) Re-insertar las preservadas manteniendo su installmentNumber original
    preserved.forEach(plan => {
      const g = this.createPaymentPlanGroup();
      g.patchValue(plan);
      g.valueChanges.subscribe(() => this.validateAssignedAmount());
      this.paymentPlans.push(g);
    });

    // 4) Calcular número inicial = max usado (paid + preserved) + 1
    const startingNumber = this.firstFreeInstallmentNumber;

    // 5) Distribuir el saldo (sin el shortfall de parciales preservadas) entre
    //    N cuotas nuevas, prorrateo igualitario
    const amounts = this.refinanceSvc.splitEvenly(amountToDistribute, newInstallmentsCount);
    const roundedAmount = amounts[0] ?? 0;

    for (let i = 0; i < newInstallmentsCount; i++) {
      const amount = amounts[i];

      const formattedAmount = amount.toLocaleString('es-CO', {
        style: 'currency', currency: 'COP',
        minimumFractionDigits: 2, maximumFractionDigits: 2
      });
      const dueDate = this.calculateNextDueDate(i + 1);
      const num = startingNumber + i;
      const observations = this.isCorporateEnrollment
        ? `Cuota ${num} - Participante (Refinanciación automática)`
        : `Cuota ${num} - Refinanciación automática`;

      const newPlanGroup = this.createPaymentPlanGroup();
      newPlanGroup.patchValue({
        id: null,
        dueDate,
        installmentNumber: num,
        amountToPay: formattedAmount,
        amountPaid: 0,
        paymentStatus: 'PENDING',
        payerType: 'PARTICIPANT',
        isPunished: false,
        observations,
        isLocked: false
      });
      newPlanGroup.valueChanges.subscribe(() => this.validateAssignedAmount());
      this.paymentPlans.push(newPlanGroup);
    }

    this.validateAssignedAmount();

    this.validateAssignedAmount();

    const formattedEstimate = roundedAmount.toLocaleString('es-CO', {
      style: 'currency', currency: 'COP',
      minimumFractionDigits: 0, maximumFractionDigits: 0
    });
    const partialNote = preserved.length > 0
      ? ` (preservadas ${preserved.length} cuota${preserved.length === 1 ? '' : 's'} parcial${preserved.length === 1 ? '' : 'es'})`
      : '';
    const startsLabel = startingNumber === 1 ? '' : ` empezando en cuota #${startingNumber}`;
    this.toastService.showToast('success',
      `Plan refinanciado en ${newInstallmentsCount} cuotas${startsLabel}${partialNote} de ~${formattedEstimate} c/u.`);
  }

  /**
   * Calcular próxima fecha de vencimiento basándose en meses futuros
   */
  private calculateNextDueDate(installmentNumber: number): Date {
    const today = new Date();
    const nextMonth = new Date(today);
    nextMonth.setMonth(today.getMonth() + installmentNumber);

    // Si el día no existe en el mes siguiente (ej: 31 de enero -> 28/29 de febrero)
    // JavaScript automáticamente ajusta la fecha
    if (nextMonth.getMonth() !== ((today.getMonth() + installmentNumber) % 12)) {
      nextMonth.setDate(0); // Va al último día del mes anterior
    }

    return nextMonth;
  }

  /**
   * Validar rango de cuotas para refinanciación
   */
  isValidRefinanceRange(): boolean {
    return this.refinanceInstallments >= this.minInstallments &&
      this.refinanceInstallments <= this.maxInstallments;
  }

  /**
   * Obtener estimado por cuota para mostrar en el modal
   */
  getEstimatedInstallmentAmount(): number {
    // Las parciales/pagadas se preservan aparte; aquí estimamos cada una
    // de las N cuotas nuevas a generar.
    if (this.refinanceInstallments <= 0) return 0;
    const distributable = this.roundToTwo(this.remainingBalance - this.getPreservedPartialShortfall());
    return this.roundToTwo(Math.max(0, distributable) / this.refinanceInstallments);
  }

  private rebuildFromBaseline(): void {
    this.totalAmount_setForm();
    this.amountPaid = this.baselineAmountPaid;
    this.remainingBalance = this.roundToTwo(this.baselineRemainingBalance);
    this.redistributionLogLines = [];

    this.paymentPlans.clear();
    [...this.baselineEditable, ...this.addedInstallments].forEach((p: any) => {
      const g = this.createPaymentPlanGroup();
      g.patchValue({
        id: p.id ?? null,
        dueDate: new Date(p.dueDate),
        installmentNumber: p.installmentNumber,
        amountToPay: p.amountToPay,
        amountPaid: p.amountPaid || 0,
        paymentStatus: p.paymentStatus || 'PENDING',
        payerType: p.payerType || 'PARTICIPANT',
        isPunished: p.isPunished || false,
        observations: p.observations || '',
        isLocked: false
      });
      g.valueChanges.subscribe(() => this.validateAssignedAmount());
      this.paymentPlans.push(g);
    });

    this.unplacedRedistributes.clear();
    this.partialInstallments.forEach(p => {
      const action = this.partialActions[p.id] || 'keep';
      const placed = this.applyPartialActionCore(p, action);
      if (!placed) this.unplacedRedistributes.add(p.id);
    });

    this.validateAssignedAmount();
  }

  /** Mantiene el totalAmount del form igual al baseline (enrollment.totalAmount). */
  private totalAmount_setForm(): void {
    this.enrollmentForm.patchValue({ totalAmount: this.baselineTotalAmount }, { emitEvent: false });
  }

  private applyPartialActionCore(plan: any, action: 'keep' | 'close-redistribute' | 'close-loss'): boolean {
    const paid = plan.amountPaid || 0;
    const original = plan.amountToPay || 0;
    const shortfall = Math.max(0, this.roundToTwo(original - paid));

    const g = this.createPaymentPlanGroup();
    if (action === 'keep') {
      g.patchValue({
        id: plan.id,
        dueDate: new Date(plan.dueDate),
        installmentNumber: plan.installmentNumber,
        amountToPay: original,
        amountPaid: paid,
        paymentStatus: 'PARTIALLY_PAID',
        payerType: plan.payerType,
        isPunished: plan.isPunished,
        observations: plan.observations,
        isLocked: false
      });
    } else {
      const obsSuffix = action === 'close-redistribute'
        ? `Cerrada en $${paid.toLocaleString('es-CO')} (era $${original.toLocaleString('es-CO')}). Saldo $${shortfall.toLocaleString('es-CO')} redistribuido.`
        : `Cerrada en $${paid.toLocaleString('es-CO')} (era $${original.toLocaleString('es-CO')}). Saldo $${shortfall.toLocaleString('es-CO')} descontado del total.`;
      g.patchValue({
        id: plan.id,
        dueDate: new Date(plan.dueDate),
        installmentNumber: plan.installmentNumber,
        amountToPay: paid,
        amountPaid: paid,
        paymentStatus: 'PAID',
        payerType: plan.payerType,
        isPunished: plan.isPunished,
        observations: this.appendObservation(plan.observations, obsSuffix),
        isLocked: true
      });
    }
    g.valueChanges.subscribe(() => this.validateAssignedAmount());
    this.paymentPlans.push(g);

    if (action === 'close-redistribute' && shortfall > 0) {
      return this.redistributeShortfall(plan, shortfall);
    }
    if (action === 'close-loss' && shortfall > 0) {
      this.remainingBalance = this.roundToTwo(this.remainingBalance - shortfall);
      this.redistributionLogLines.push(
        `Cuota #${plan.installmentNumber}: cerrada en $${paid.toLocaleString('es-CO')}; saldo $${shortfall.toLocaleString('es-CO')} marcado como pérdida (no redistribuido).`
      );
    }
    return true;
  }

  /**
   * 🆕 Aplicar la acción elegida por el operador para una cuota PARTIALLY_PAID:
   *   - 'keep'                → la deja como está en el FormArray (saldo pendiente real)
   *   - 'close-redistribute'  → la cierra al monto cobrado y reparte el saldo
   *                             entre las cuotas PENDING posteriores (prorrateo igual)
   *   - 'close-loss'          → la cierra al monto cobrado y descuenta el saldo
   *                             del total (no se redistribuye)
   * @param plan       cuota PARTIAL del snapshot
   * @param action     acción elegida
   * @param showToast  si mostrar feedback al usuario (false durante init)
   */
  applyPartialAction(plan: any, action: 'keep' | 'close-redistribute' | 'close-loss', showToast = true): void {
    this.partialActions[plan.id] = action;
    this.rebuildFromBaseline();

    const shortfall = Math.max(0, this.roundToTwo((plan.amountToPay || 0) - (plan.amountPaid || 0)));

    if (action === 'close-redistribute' && shortfall > 0 && this.unplacedRedistributes.has(plan.id)) {
      this.openCreateInstallmentModal(plan);
      return;
    }

    if (showToast && action === 'close-redistribute' && shortfall > 0) {
      this.toastService.showToast('success', `Saldo de $${shortfall.toLocaleString('es-CO')} redistribuido.`);
    }
    if (showToast && action === 'close-loss' && shortfall > 0) {
      this.toastService.showToast('warning', `Saldo de $${shortfall.toLocaleString('es-CO')} descontado del total.`);
    }
  }

  private redistributeShortfall(partialPlan: any, shortfall: number): boolean {
    const candidates = this.paymentPlans.controls.filter(c => {
      const status = c.get('paymentStatus')?.value;
      const num = c.get('installmentNumber')?.value;
      return (status === 'PENDING' || status === 'OVERDUE' || status === 'LINK_GENERATED')
        && num > partialPlan.installmentNumber
        && !c.get('isLocked')?.value;
    });
    if (candidates.length === 0) return false;

    const deltas = this.refinanceSvc.splitEvenly(shortfall, candidates.length);
    const affected: string[] = [];
    candidates.forEach((ctrl, i) => {
      const delta = deltas[i];
      const current = this.parseFormattedCurrency(ctrl.get('amountToPay')?.value || '0');
      ctrl.get('amountToPay')?.setValue(this.roundToTwo(current + delta), { emitEvent: false });
      affected.push(`#${ctrl.get('installmentNumber')?.value} (+$${delta.toLocaleString('es-CO')})`);
    });
    this.redistributionLogLines.push(
      `Cuota #${partialPlan.installmentNumber}: cerrada en $${(partialPlan.amountPaid || 0).toLocaleString('es-CO')}. ` +
      `Saldo $${shortfall.toLocaleString('es-CO')} redistribuido en ${affected.join(', ')}.`
    );
    return true;
  }

  private openCreateInstallmentModal(plan: any): void {
    this.pendingPartialPlan = plan;
    this.pendingShortfall = Math.max(0, this.roundToTwo((plan.amountToPay || 0) - (plan.amountPaid || 0)));
    this.newInstallmentDate = this.defaultNextDueDateInput();
    this.showCreateInstallmentModal = true;
  }

  private defaultNextDueDateInput(): string {
    const times = [
      ...this.paymentPlans.controls.map(c => new Date(c.get('dueDate')?.value).getTime()),
      ...this.paidInstallments.map(p => new Date(p.dueDate).getTime())
    ].filter(t => !isNaN(t));
    const base = times.length ? new Date(Math.max(...times)) : new Date();
    base.setMonth(base.getMonth() + 1);
    const y = base.getFullYear();
    const m = ('0' + (base.getMonth() + 1)).slice(-2);
    const d = ('0' + base.getDate()).slice(-2);
    return `${y}-${m}-${d}`;
  }

  confirmCreateInstallment(): void {
    if (!this.pendingPartialPlan) return;
    // num se calcula ANTES de pushear; firstFreeInstallmentNumber lee el FormArray
    // ya reconstruido del rebuild previo, así dos cuotas auto-creadas no colisionan.
    const num = this.firstFreeInstallmentNumber;
    this.addedInstallments.push({
      id: null,
      dueDate: this.newInstallmentDate,
      installmentNumber: num,
      amountToPay: 0, // arranca en 0: redistributeShortfall (en el rebuild) la llena con el saldo. Sembrarla con el shortfall lo contaría doble.
      amountPaid: 0,
      paymentStatus: 'PENDING',
      payerType: 'PARTICIPANT',
      isPunished: false,
      observations: `Cuota creada para absorber saldo de cuota #${this.pendingPartialPlan.installmentNumber}`
    });
    const created = num;
    const amount = this.pendingShortfall;
    this.pendingPartialPlan = null;
    this.showCreateInstallmentModal = false;
    this.rebuildFromBaseline();
    this.toastService.showToast('success',
      `Cuota #${created} creada por $${amount.toLocaleString('es-CO')} para absorber el saldo.`);
  }

  cancelCreateInstallment(): void {
    if (this.pendingPartialPlan) this.partialActions[this.pendingPartialPlan.id] = 'keep';
    this.pendingPartialPlan = null;
    this.showCreateInstallmentModal = false;
    this.rebuildFromBaseline();
  }

  private appendObservation(existing: string | null | undefined, suffix: string): string {
    if (!existing) return suffix;
    return existing.includes(suffix) ? existing : `${existing} | ${suffix}`;
  }

  togglePaidSection(): void { this.showPaidSection = !this.showPaidSection; }
  togglePartialSection(): void { this.showPartialSection = !this.showPartialSection; }

  onSubmit(): void {
    if (this.enrollmentForm.invalid || this.exceedsTotalAmount) {
      this.toastService.showToast('error', 'Formulario inválido o monto excedido');
      return;
    }
    // Antes de pegarle al backend, pedimos motivo + comentario obligatorio.
    this.selectedReason = PaymentPlanVersionReason.Refinance;
    this.reasonComment = '';
    this.showReasonModal = true;
  }

  closeReasonModal(): void {
    this.showReasonModal = false;
  }

  confirmReasonAndSave(): void {
    if (!this.reasonComment || this.reasonComment.trim().length < 5) {
      this.toastService.showToast('error', 'Debes escribir un comentario que justifique el cambio (mínimo 5 caracteres).');
      return;
    }

    // 🆕 Incluimos también las cuotas PAID (read-only) en el payload para que
    // el backend reciba el plan COMPLETO y pueda validarlo correctamente
    // (sino las consideraría como "eliminadas" y bloquearía con la regla
    // "no podés borrar cuotas con recibos asociados").
    const editableInstallments = this.paymentPlans.value.map((plan: any) => {
      const cleanAmount = this.parseFormattedCurrency(plan.amountToPay);
      return {
        dueDate: plan.dueDate,
        installmentNumber: plan.installmentNumber,
        amountToPay: cleanAmount,
        paymentStatus: plan.paymentStatus,
        payerType: plan.payerType,
        isPunished: plan.isPunished || false,
        observations: plan.observations || ''
      };
    });

    const paidInstallmentsPayload = this.paidInstallments.map((p: any) => ({
      dueDate: p.dueDate,
      installmentNumber: p.installmentNumber,
      amountToPay: p.amountToPay,
      paymentStatus: 'PAID',
      payerType: p.payerType,
      isPunished: p.isPunished || false,
      observations: p.observations || ''
    }));

    // Ordenamos por número para mantener consistencia visual / matching backend
    const allPayments = [...paidInstallmentsPayload, ...editableInstallments]
      .sort((a, b) => a.installmentNumber - b.installmentNumber);

    const payload = {
      enrollmentId: this.enrollmentId,
      totalAmount: Number(this.enrollmentForm.get('totalAmount')?.value),
      paymentPlans: allPayments,
      versionReason: this.selectedReason,
      versionReasonComment: this.reasonComment.trim(),
      redistributionLog: this.redistributionLogLines.length > 0
        ? this.redistributionLogLines.join('\n')
        : null
    };

    this.showReasonModal = false;
    this.loadingService.show();

    this.enrollmentService.refinance(this.enrollmentId, payload)
      .pipe(finalize(() => this.loadingService.hide()))
      .subscribe({
        next: (res: any) => {
          this.toastService.showToast('success', 'Plan de pagos actualizado. Nueva versión creada.');
          const excesses = res?.data?.generatedExcesses ?? [];
          if (excesses.length === 0) {
            // 🆕 Redirigir a la inscripción original (no a la lista).
            //    Si el caller pasó `returnTo` en queryParams se respeta; si no,
            //    fallback al view v2 de esta inscripción.
            this.redirectAfterSave();
            return;
          }
          // Hay excedentes generados: mostrar resumen y redirigir al cerrarlo.
          this.excessSummary = excesses;
        },
        error: (err) => {
          console.error('❌ ERROR AL GUARDAR:', err);
          const msg = err?.error?.message || err?.error || 'Error al guardar los cambios.';
          this.toastService.showToast('error', msg);
        }
      });
  }

  /** Cierra el modal de excedentes generados y continúa con la redirección. */
  onExcessSummaryClosed(): void {
    this.excessSummary = [];
    this.redirectAfterSave();
  }

  /** Vista previa del sobrepago de las cuotas ya pagadas (se convierte en excedente al guardar). */
  get overpaymentPreview(): { count: number; total: number } {
    return this.refinanceSvc.overpaymentPreview(
      this.paidInstallments.map(p => ({
        amountToPay: Number(p.amountToPay || 0),
        amountPaid: Number(p.amountPaid || 0)
      }))
    );
  }

  /**
   * 🆕 Redirige al view de la inscripción original tras guardar.
   * Si el caller pasó `?returnTo=/dashboard/...` en queryParams y es una URL interna válida,
   * se respeta; si no, fallback al view v2 de esta inscripción.
   * Whitelist (`startsWith('/dashboard/')`) previene open-redirect a URLs externas.
   */
  private redirectAfterSave(): void {
    const returnTo = this.route.snapshot.queryParamMap.get('returnTo');
    if (returnTo && returnTo.startsWith('/dashboard/')) {
      this.router.navigateByUrl(returnTo);
      return;
    }
    this.router.navigate(['/dashboard/enrollments/view-v2', this.enrollmentId]);
  }

  /**
   * 💰 Convertir string de moneda formateada a número decimal
   */
  private parseFormattedCurrency(formattedValue: string | number): number {
    return this.refinanceSvc.parseFormattedCurrency(formattedValue);
  }

  /**
   * 🔧 Método de debug para forzar validación manual desde la consola
   * Usar en la consola: component.debugValidation()
   */
  debugValidation(): void {
    console.log('🔧 DEBUG MANUAL ACTIVADO');
    console.log('🔧 Componente actual:', this);
    console.log('🔧 FormArray paymentPlans:', this.paymentPlans);
    console.log('🔧 Controles actuales:', this.paymentPlans.controls.length);

    // Mostrar valores de cada cuota
    this.paymentPlans.controls.forEach((control, index) => {
      const amountControl = control.get('amountToPay');
      console.log(`🔧 Cuota ${index + 1}:`, {
        value: amountControl?.value,
        type: typeof amountControl?.value,
        valid: amountControl?.valid,
        errors: amountControl?.errors
      });
    });

    console.log('🔧 remainingBalance:', this.remainingBalance);
    console.log('🔧 totalAssignedAmount ANTES:', this.totalAssignedAmount);

    // Forzar validación
    this.validateAssignedAmount();

    console.log('🔧 totalAssignedAmount DESPUÉS:', this.totalAssignedAmount);
  }
}
